"use client";

import Image from "next/image";
import { useState } from "react";
import { Icon } from "./icon";
import type { AdGoogleMirroredReview } from "@to/db";
import { ProfilePagination } from "./profile-pagination";

const PAGE_SIZE = 6;
const DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
});

export interface GoogleReviewsSectionData {
  locationTitle: string;
  officialRating: number;
  officialReviewCount: number;
  reviews: AdGoogleMirroredReview[];
}

function reviewDate(value: string): string | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : DATE_FORMAT.format(date);
}

function ReviewerAvatar({ review }: { review: AdGoogleMirroredReview }) {
  if (review.reviewerPhotoUrl) {
    return (
      <Image
        src={review.reviewerPhotoUrl}
        alt=""
        aria-hidden="true"
        width={44}
        height={44}
        unoptimized
        className="h-11 w-11 rounded-full object-cover"
      />
    );
  }
  const initial = review.reviewerIsAnonymous
    ? "A"
    : review.reviewerName.trim().charAt(0).toUpperCase() || "A";
  return (
    <span
      aria-hidden="true"
      className="flex h-11 w-11 items-center justify-center rounded-full text-sm font-semibold"
      style={{
        backgroundColor: "var(--brand-surface-subtle)",
        color: "var(--brand-primary)",
      }}
    >
      {initial}
    </span>
  );
}

function Stars({ rating }: { rating: number }) {
  return (
    <span
      className="inline-flex items-center gap-0.5"
      role="img"
      aria-label={`${rating} out of 5 stars`}
    >
      {Array.from({ length: 5 }, (_, index) => (
        <Icon
          key={index}
          name="star"
          size={15}
          filled={index < rating}
          style={{
            color:
              index < rating ? "var(--brand-sun)" : "var(--brand-card-border)",
          }}
          aria-hidden
        />
      ))}
    </span>
  );
}

export function GoogleReviewsSection({
  data,
}: {
  data: GoogleReviewsSectionData;
}) {
  const [page, setPage] = useState(1);
  if (data.officialReviewCount <= 0) return null;

  const totalPages = Math.ceil(data.reviews.length / PAGE_SIZE);
  const visibleReviews = data.reviews.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  return (
    <section
      aria-label="Reviews from Google"
      className="p-6 sm:p-8"
      style={{
        backgroundColor: "var(--brand-card-bg)",
        border: "1px solid var(--brand-card-border)",
        borderRadius: "var(--radius-card, 8px)",
        boxShadow: "var(--shadow-resting)",
      }}
    >
      <header className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
        <div className="min-w-0">
          <h2
            className="text-xl font-semibold"
            style={{
              color: "var(--brand-text)",
              fontFamily: "var(--brand-font-heading)",
            }}
          >
            Reviews from Google
          </h2>
          <p
            className="mt-1 break-words text-sm"
            style={{ color: "var(--brand-text-muted)" }}
          >
            Official Google rating for {data.locationTitle}
          </p>
        </div>

        <div
          className="inline-flex shrink-0 items-center gap-2 tabular-nums"
          aria-label={`${data.officialRating.toFixed(1)} stars from ${data.officialReviewCount} Google reviews`}
        >
          <Icon
            name="star"
            size={20}
            filled
            style={{ color: "var(--brand-sun)" }}
            aria-hidden
          />
          <span
            className="text-lg font-semibold"
            style={{ color: "var(--brand-text)" }}
          >
            {data.officialRating.toFixed(1)}
          </span>
          <span
            className="text-sm"
            style={{ color: "var(--brand-text-muted)" }}
          >
            ({data.officialReviewCount.toLocaleString("en-US")}
            {data.officialReviewCount === 1 ? " review" : " reviews"})
          </span>
        </div>
      </header>

      {visibleReviews.length > 0 && (
        <ul className="mt-6 divide-y divide-[var(--brand-card-border)]">
          {visibleReviews.map((review) => {
            const date = reviewDate(review.createTime);
            const reviewerName = review.reviewerIsAnonymous
              ? "Anonymous"
              : review.reviewerName.trim() || "Anonymous";
            return (
              <li
                key={review.reviewId}
                className="grid min-w-0 grid-cols-[44px_minmax(0,1fr)] gap-3 py-5 first:pt-0 last:pb-0"
              >
                <ReviewerAvatar review={review} />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <p
                      className="min-w-0 break-words text-sm font-semibold"
                      style={{ color: "var(--brand-text)" }}
                    >
                      {reviewerName}
                    </p>
                    <Stars rating={review.rating} />
                    {date && (
                      <time
                        dateTime={review.createTime}
                        className="text-xs"
                        style={{ color: "var(--brand-text-muted)" }}
                      >
                        {date}
                      </time>
                    )}
                  </div>

                  {review.comment && (
                    <p
                      className="mt-2 break-words text-[15px] leading-relaxed"
                      style={{ color: "var(--brand-text)" }}
                    >
                      {review.comment}
                    </p>
                  )}

                  {review.reply?.comment && (
                    <div
                      className="mt-3 rounded-lg px-4 py-3"
                      style={{ backgroundColor: "var(--brand-surface-subtle)" }}
                    >
                      <p
                        className="text-xs font-semibold"
                        style={{ color: "var(--brand-primary)" }}
                      >
                        Response from {data.locationTitle}
                      </p>
                      <p
                        className="mt-1 break-words text-sm leading-relaxed"
                        style={{ color: "var(--brand-text)" }}
                      >
                        {review.reply.comment}
                      </p>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <ProfilePagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        ariaLabel="Google review pages"
      />

      <p className="mt-6 text-xs" style={{ color: "var(--brand-text-muted)" }}>
        Review data provided by Google Business Profile.
      </p>
    </section>
  );
}
